import React, { Suspense, lazy } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, Stack } from '@mui/material'

// Historia 148 (S57/R4) — EL RECTÁNGULO DEL DOCUMENTO, CARGADO DE FORMA DIFERIDA.
//
// Es la MISMA pieza que usa el widget de instrucciones de tarea y la lupa de MIC:
// `DocumentsCarouselView` — visor grande, tira de miniaturas para saltar entre los documentos del
// mismo registro, anterior/siguiente y pantalla completa. Reutilizarla es lo que mantiene el formato
// que el resto de MIC ya tiene, en vez de inventar aquí un visor distinto.
//
// ⚠️ SE LE PASAN LOS DOCUMENTOS, NO SE LOS BUSCA ÉL. El carrusel, por su cuenta, consultaría
// `document/getAll/<tipo>/<id>`: un camino de MIC oficina, sin contexto MOS, que devolvería TODOS
// los documentos del registro —incluidos los que Sun filtró por permiso (RN16)— y que el gate del
// servidor ni siquiera mira (S44/D2). Aquí se le entregan exactamente las entradas que el endpoint
// agregador ya autorizó para ese registro.
//
// ⚠️ `DocumentsCarouselView` importa el visor de forma ESTÁTICA, y con él ~600 KB de three.js. Por
// eso entra por `React.lazy`: su chunk solo se descarga cuando el operario abre un documento, nunca
// al abrir el modal. Es la invariante de code-splitting del módulo documental (R4).
const LazyDocumentsCarouselView = lazy(() =>
  import('../../../../configuration/document/DocumentsCarouselView')
)

/**
 * @param {Object} props
 * @param {Array} props.documents Los documentos del registro, tal y como los autorizó el servidor
 * @param {string} props.initialDocumentUUID El que el operario acaba de pulsar
 * @param {string} props.contextQuery Los 4 query params del contexto MOS de la aparición pulsada
 * @param {Function} props.onLoadError Recibe `{ status, message }` cuando el contenido no llega
 * @param {Function} props.onDocumentChange Recibe el documento que el carrusel está mostrando
 */
const FabricationDocumentViewer = ({
  documents = [],
  initialDocumentUUID = '',
  contextQuery = '',
  onLoadError = () => {},
  onDocumentChange = () => {}
}) => (
  // RN27: mientras el chunk llega, indicador — nunca un hueco en blanco (S58).
  <Suspense
    fallback={
      <Stack alignItems='center' justifyContent='center' sx={{ minHeight: 200 }}>
        <CircularProgress />
      </Stack>
    }
  >
    <LazyDocumentsCarouselView
      documents={documents}
      initialDocumentUUID={initialDocumentUUID}
      contextQuery={contextQuery}
      onLoadError={onLoadError}
      onDocumentChange={onDocumentChange}
      // El modal ya tiene su propia cabecera y sus propias acciones por fila: aquí solo interesa el
      // rectángulo. Y escribir desde el carrusel iría por el camino SIN contexto MOS, así que no.
      showDocumentsHeader={false}
      enableUpload={false}
      enableReorder={false}
    />
  </Suspense>
)

FabricationDocumentViewer.propTypes = {
  documents: PropTypes.array,
  initialDocumentUUID: PropTypes.string,
  contextQuery: PropTypes.string,
  onLoadError: PropTypes.func,
  onDocumentChange: PropTypes.func
}

export default FabricationDocumentViewer
